import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useActingAthlete } from '../context/ActingAthleteContext';

/**
 * Whose plan is a web billing call for?
 *
 * The billing service takes `athlete_user_id` as the ENTITLEMENT TARGET and
 * records the JWT caller as the payer (authorize_payment_for_athlete in
 * billing-service/app/auth.py). When `athlete_user_id` is omitted it falls
 * back to `user_id` — which is how guardian 2910 paid $19.99 for his own
 * account on 2026-09-22 instead of his athlete's. Every web billing call
 * therefore sends the id this module resolves.
 *
 *   athlete / self-paying user                     → their own id
 *   guardian acting as an athlete (actingAthleteId) → that athlete
 *   guardian with exactly one linked athlete        → that athlete
 *   guardian with several and none picked           → null + needsPicker
 *
 * An explicit choice (`selectedAthleteId` — the picker, or /upgrade's
 * ?athlete_id= from the parent-welcome link) wins over all of the guardian
 * rules, but ONLY when it is one of the guardian's linked athletes; an
 * unlinked id is ignored and the rules above apply.
 *
 * A guardian NEVER resolves to their own id: with no athletes (or the
 * roster still loading) the answer is null, and callers must not start a
 * billing action without a target.
 */
export function resolveBillingTarget({
  user,
  actingAthleteId = null,
  managedAthletes = [],
  selectedAthleteId = null,
} = {}) {
  if (!user || user.id == null) return { athleteUserId: null, needsPicker: false };
  // Role, not profile presence, decides guardian-ness (same check as
  // DashboardPage/Layout). A stale acting id left in localStorage never
  // redirects a non-guardian's billing.
  if (user.role !== 'guardian') return { athleteUserId: user.id, needsPicker: false };

  const roster = Array.isArray(managedAthletes) ? managedAthletes : [];
  if (selectedAthleteId != null) {
    const chosen = roster.find((a) => a.athlete_user_id === selectedAthleteId);
    if (chosen) return { athleteUserId: chosen.athlete_user_id, needsPicker: false };
  }

  if (actingAthleteId != null) return { athleteUserId: actingAthleteId, needsPicker: false };

  if (roster.length === 1) return { athleteUserId: roster[0].athlete_user_id, needsPicker: false };
  if (roster.length > 1) return { athleteUserId: null, needsPicker: true };
  return { athleteUserId: null, needsPicker: false };
}

/**
 * Request body for a billing-service POST. `user_id` stays for legacy
 * compatibility; `athlete_user_id` is the entitlement target. Throws rather
 * than silently falling back to the caller.
 */
export function billingBody(user, athleteUserId, extra = {}) {
  if (athleteUserId == null) {
    throw new Error('No athlete selected for this billing action');
  }
  return { user_id: user?.id, athlete_user_id: athleteUserId, ...extra };
}

export function athleteDisplayName(a) {
  return [a.athlete_first_name, a.athlete_last_name].filter(Boolean).join(' ')
    || a.athlete_email
    || `Athlete ${a.athlete_user_id}`;
}

/**
 * Whose plan is on screen? Names the resolved target so a guardian managing
 * a plan can see WHOSE it is. The name comes from the roster
 * useBillingTarget already loaded (GET /guardians/my-athletes) — no extra
 * endpoint. A guardian who is *acting* as an athlete never loads that roster,
 * so ActingAthleteContext's name (itself derived from the same roster record
 * at enterAthlete time) is the fallback.
 */
export function billingTargetName({
  athleteUserId,
  managedAthletes = [],
  actingAthleteName = null,
} = {}) {
  if (athleteUserId == null) return null;
  const match = (Array.isArray(managedAthletes) ? managedAthletes : [])
    .find((a) => a.athlete_user_id === athleteUserId);
  if (match) return athleteDisplayName(match);
  return actingAthleteName || `Athlete ${athleteUserId}`;
}

/** Parse an id from a query string; anything non-numeric → null. */
export function parseAthleteIdParam(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * React binding. Loads a guardian's linked athletes from the existing
 * GET /guardians/my-athletes (the same roster DashboardPage and
 * GuardianManagePage use) only when it is needed — a guardian who is not
 * currently acting as an athlete, or who arrived with a requested athlete
 * (`requestedAthleteId`, e.g. /upgrade?athlete_id=) that must be checked
 * against that roster before it is honored.
 */
export function useBillingTarget({ requestedAthleteId = null } = {}) {
  const { user, API_URL } = useAuth();
  const { actingAthleteId } = useActingAthlete();
  const isGuardian = user?.role === 'guardian';
  const needRoster = isGuardian && (actingAthleteId == null || requestedAthleteId != null);

  const [managedAthletes, setManagedAthletes] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(needRoster);
  const [rosterFailed, setRosterFailed] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);

  useEffect(() => {
    if (!needRoster) {
      setRosterLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRosterLoading(true);
    setRosterFailed(false);
    Promise.resolve(axios.get(`${API_URL}/guardians/my-athletes`))
      .then((res) => { if (!cancelled) setManagedAthletes(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (!cancelled) { setManagedAthletes([]); setRosterFailed(true); } })
      .finally(() => { if (!cancelled) setRosterLoading(false); });
    return () => { cancelled = true; };
  }, [needRoster, API_URL, user?.id]);

  // The picker's pick beats the requested id; resolveBillingTarget drops
  // either one if it is not a linked athlete.
  const resolved = resolveBillingTarget({
    user, actingAthleteId, managedAthletes,
    selectedAthleteId: selectedAthleteId ?? requestedAthleteId,
  });
  // While the roster needed to validate a choice is loading, there is no
  // target yet — otherwise an acting guardian who arrived with a requested
  // athlete could check out for the acting one in the gap.
  const athleteUserId = needRoster && rosterLoading ? null : resolved.athleteUserId;
  const { needsPicker } = resolved;
  const requestedIsLinked = requestedAthleteId != null
    && managedAthletes.some((a) => a.athlete_user_id === requestedAthleteId);

  return {
    athleteUserId,
    needsPicker,
    loading: rosterLoading,
    // A guardian with the roster loaded and nobody linked: nothing to pay for.
    noLinkedAthletes: needRoster && !rosterLoading && !rosterFailed && managedAthletes.length === 0,
    rosterFailed: needRoster && rosterFailed,
    managedAthletes,
    // The effective explicit choice, so the picker shows a linked
    // ?athlete_id= as already checked.
    selectedAthleteId: selectedAthleteId ?? (requestedIsLinked ? requestedAthleteId : null),
    selectAthlete: setSelectedAthleteId,
  };
}
